import React, { useContext, useMemo } from "react";

import { mdiPlus } from "@mdi/js";
import Icon from "@mdi/react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import languageCodeList from "../../constants/languages";
import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";
import Dropdown from "../dropdown/Dropdown";

import type { JSX } from "react";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { AppState } from "../../reducers";
import type { DropdownItem } from "../dropdown/Dropdown";

interface FormAddLanguageProps {
  languageList?: LanguageCode[];
}

export default function FormAddLanguage({
  languageList,
}: FormAddLanguageProps): JSX.Element | null {
  const { t } = useTranslation("languages");
  const { fieldArray, addLanguage, isAddingLanguage } = useContext(
    FormTranslationsContext,
  );
  const languages = useSelector(
    (state: AppState) => state.fetchReducer.languages,
  );

  const { projectSlug } = useParams<{ projectSlug: string }>();

  const projects = useSelector(
    (state: AppState) => state.fetchReducer.projects,
  );

  const projectLanguageList = projects.data
    .find((project) => project.name === projectSlug)
    ?.translations?.map((tr) => tr.language);

  const resolveLanguageUuid = (code: LanguageCode): string | undefined =>
    languages.data?.find(
      (l: { language: string; uuid: string }) => l.language === code,
    )?.uuid;

  const handleAddLanguage = async (
    languageCode: LanguageCode,
  ): Promise<void> => {
    try {
      const languageUuid = resolveLanguageUuid(languageCode);

      if (!languageUuid) {
        throw new Error(
          `Language UUID not found for code ${languageCode}. Cannot add translation.`,
        );
      }

      await addLanguage({
        languageCode,
        languageUuid,
      });
    } catch (error) {
      console.error("Error adding language:", error);
    }
  };

  const languageOptions = useMemo(() => {
    const existingLanguageList = fieldArray?.fields.map(
      (field) => field.language,
    );

    const toDropdownOption = (
      languageCode: LanguageCode,
    ): DropdownItem<LanguageCode> => ({
      value: languageCode,
      label: t(`langs.${languageCode}`),
    });

    const sortOptions = (
      options: DropdownItem<LanguageCode>[],
    ): DropdownItem<LanguageCode>[] =>
      options.sort((a, b) =>
        (a.label as string).localeCompare(b.label as string),
      );

    if (languageList) {
      return sortOptions(
        languageList
          .filter((code) => !existingLanguageList?.includes(code))
          .map(toDropdownOption),
      );
    }

    const projectOptions = sortOptions(
      (projectLanguageList ?? [])
        .filter((code) => !existingLanguageList?.includes(code))
        .map(toDropdownOption),
    );

    const restOptions = sortOptions(
      languageCodeList
        .filter((code) => !projectLanguageList?.includes(code))
        .filter((code) => !existingLanguageList?.includes(code))
        .map(toDropdownOption),
    );

    return [...projectOptions, ...restOptions];
  }, [fieldArray?.fields, t, languageList, projectLanguageList]);

  if (languageOptions?.length <= 0) return null;

  const dropdownItems = Array.from(languageOptions).map((option) => ({
    value: option.value,
    label: (
      <div className="text-muted-foreground hover:bg-muted flex w-full items-center justify-between gap-[0.625rem] px-[0.9375rem] py-[0.46875rem]">
        <div>{option.label}</div>
        <div className="text-muted-foreground uppercase">{option.value}</div>
      </div>
    ),
    searchValue: `${option.label} ${option.value}`,
  }));

  return (
    <Dropdown
      withoutChevron
      withSearch
      align="right"
      className="mt-[0.625rem] [&>div>div:first-child]:text-center [&>div>div:first-child]:tracking-wider [&>div>div:first-child]:uppercase"
      disabled={isAddingLanguage}
      icon={<Icon path={mdiPlus} size={0.9375} />}
      items={dropdownItems}
      noResultsText={t("form:searchLanguageNoResults")}
      onClick={handleAddLanguage}
      searchPlaceholder={t("form:searchLanguagePlaceholder")}
      title={t("selectLanguage")}
    />
  );
}
